import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useEffect } from 'react';

// --- Plans ---
const studentPlans = [
  { duration: '3 Months', days: 90, monthlyFee: 1800, admissionFee: null },
  { duration: '6 Months', days: 180, monthlyFee: 3500, admissionFee: null },
  { duration: '12 Months', days: 365, monthlyFee: 6000, admissionFee: null },
  { duration: '1 Month', days: 30, monthlyFee: 600, admissionFee: 1000 },
  { duration: '1 Day', days: 1, monthlyFee: 60, admissionFee: null },
  { duration: '7 Days', days: 7, monthlyFee: 600, admissionFee: null },
  { duration: '15 Days', days: 15, monthlyFee: 800, admissionFee: null },
];

const nonStudentPlans = [
  { duration: '3 Months', days: 90, monthlyFee: 2000, admissionFee: null },
  { duration: '6 Months', days: 180, monthlyFee: 3800, admissionFee: null },
  { duration: '12 Months', days: 365, monthlyFee: 7000, admissionFee: null },
  { duration: '1 Month', days: 30, monthlyFee: 700, admissionFee: 1000 },
  { duration: '1 Day', days: 1, monthlyFee: 70, admissionFee: null },
  { duration: '7 Days', days: 7, monthlyFee: 800, admissionFee: null },
  { duration: '15 Days', days: 15, monthlyFee: 1000, admissionFee: null },
];

const personalTraining = [
  { description: '6 Classes (Dummy Description)', fee: 2000 },
  { description: '8 Classes (Dummy Description)', fee: 3000 },
];

// --- Schema ---
const schema = z.object({
  name: z.string().min(1, "Name is required"),
  phone: z.string().min(1, "Phone is required"),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  category: z.enum(['Student', 'Non-Student'], { required_error: "Category is required" }),
  plan: z.string().min(1, "Plan is required"),
  start_date: z.string().min(1, "Start date is required"),
  expiry_date: z.string().min(1, "Expiry date is required"),
  personal_training: z.string().optional(),
  payment_status: z.enum(['Paid', 'Pending'], { required_error: "Payment status is required" }),
  pending_amount: z.string().optional().refine(
    (val) => !val || (!isNaN(parseFloat(val)) && parseFloat(val) > 0),
    { message: "Pending amount must be a positive number" }
  ),
}).refine(
  (data) => data.payment_status !== "Pending" || (data.pending_amount && data.pending_amount.trim() !== ""),
  { message: "Pending amount is required when payment status is Pending", path: ["pending_amount"] }
);

const MemberForm = ({ onSubmit, editingMember }) => {
  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      payment_status: "Paid",
      pending_amount: "",
    },
  });

  const category = watch("category");
  const plan = watch("plan");
  const startDate = watch("start_date");
  const paymentStatus = watch("payment_status");

  // --- When Editing a Member ---
  useEffect(() => {
    if (editingMember) {
      let formData = { ...editingMember, pending_amount: "" };

      // ✅ Fix date format for <input type="date">
      if (editingMember.start_date) {
        formData.start_date = new Date(editingMember.start_date).toISOString().split("T")[0];
      }
      if (editingMember.expiry_date) {
        formData.expiry_date = new Date(editingMember.expiry_date).toISOString().split("T")[0];
      }

      // ✅ Handle pending amount if exists
      if (editingMember.payment_status.startsWith("Pending")) {
        formData.payment_status = "Pending";
        const match = editingMember.payment_status.match(/₹(\d+)/);
        if (match) {
          formData.pending_amount = match[1];
        }
      } else {
        formData.payment_status = "Paid";
      }

      reset(formData);
    } else {
      reset({ payment_status: "Paid", pending_amount: "" });
    }
  }, [editingMember, reset]);

  // --- Plan options based on category ---
  let plans = [];
  if (category === "Student") {
    plans = studentPlans.map(p => ({
      label: `${p.duration} - ₹${p.monthlyFee}${p.admissionFee ? ` + Admission ₹${p.admissionFee}` : ''}`,
      value: p.duration,
      days: p.days
    }));
  } else if (category === "Non-Student") {
    plans = nonStudentPlans.map(p => ({
      label: `${p.duration} - ₹${p.monthlyFee}${p.admissionFee ? ` + Admission ₹${p.admissionFee}` : ''}`,
      value: p.duration,
      days: p.days
    }));
  }

  // --- Auto-calculate expiry date ---
  useEffect(() => {
    if (!startDate || !plan || !category) return;
    let selectedPlan = null;
    if (category === "Student") {
      selectedPlan = studentPlans.find(p => p.duration === plan);
    } else if (category === "Non-Student") {
      selectedPlan = nonStudentPlans.find(p => p.duration === plan);
    }
    if (selectedPlan) {
      const start = new Date(startDate);
      const expiry = new Date(start);
      expiry.setDate(expiry.getDate() + selectedPlan.days);
      setValue("expiry_date", expiry.toISOString().split("T")[0]);
    }
  }, [plan, startDate, category, setValue]);

  // --- Submit handler ---
  const handleFormSubmit = (data) => {
    console.log('Form submission data:', data);
    const submissionData = { ...data };
    if (submissionData.payment_status === "Pending") {
      if (submissionData.pending_amount && submissionData.pending_amount.trim() !== "") {
        submissionData.payment_status = `Pending (₹${submissionData.pending_amount})`;
      } else {
        submissionData.payment_status = "Pending";
      }
    }
    delete submissionData.pending_amount;

    onSubmit(submissionData);

    // ✅ Reset after submission
    reset({
      payment_status: "Paid",
      pending_amount: "",
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="bg-white p-6 rounded-lg shadow mb-8">
      <input {...register('name')} placeholder="Name" className="border p-2 mb-2 w-full" />
      {errors.name && <p className="text-red-500">{errors.name.message}</p>}

      <input {...register('phone')} placeholder="Phone" className="border p-2 mb-2 w-full" />
      {errors.phone && <p className="text-red-500">{errors.phone.message}</p>}

      <input {...register('email')} placeholder="Email (optional)" className="border p-2 mb-2 w-full" />
      {errors.email && <p className="text-red-500">{errors.email.message}</p>}

      {/* Category */}
      <select {...register('category')} className="border p-2 mb-2 w-full">
        <option value="">Select Category</option>
        <option value="Student">Student</option>
        <option value="Non-Student">Non-Student</option>
      </select>
      {errors.category && <p className="text-red-500">{errors.category.message}</p>}

      {/* Plan */}
      <select {...register('plan')} className="border p-2 mb-2 w-full" disabled={!category}>
        <option value="">Select Plan</option>
        {plans.map((p, idx) => (
          <option key={idx} value={p.value}>{p.label}</option>
        ))}
      </select>
      {errors.plan && <p className="text-red-500">{errors.plan.message}</p>}

      {/* Personal Training (Only available if a main plan is chosen) */}
      {plan && (
        <select {...register('personal_training')} className="border p-2 mb-2 w-full">
          <option value="">No Personal Training</option>
          {personalTraining.map((p, idx) => (
            <option key={idx} value={p.description}>
              {p.description} - ₹{p.fee}
            </option>
          ))}
        </select>
      )}

      {/* Payment Status */}
      <select {...register('payment_status')} className="border p-2 mb-2 w-full">
        <option value="Paid">Paid</option>
        <option value="Pending">Pending</option>
      </select>
      {errors.payment_status && <p className="text-red-500">{errors.payment_status.message}</p>}

      {/* Pending Amount */}
      {paymentStatus === "Pending" && (
        <input
          type="number"
          {...register('pending_amount')}
          placeholder="Enter Pending Amount"
          className="border p-2 mb-2 w-full"
        />
      )}
      {errors.pending_amount && <p className="text-red-500">{errors.pending_amount.message}</p>}

      <input type="date" {...register('start_date')} className="border p-2 mb-2 w-full" />
      {errors.start_date && <p className="text-red-500">{errors.start_date.message}</p>}

      <input type="date" {...register('expiry_date')} className="border p-2 mb-2 w-full" readOnly />
      {errors.expiry_date && <p className="text-red-500">{errors.expiry_date.message}</p>}

      <button
        type="submit"
        className="bg-primary-950 text-white px-4 py-2 rounded hover:bg-primary/90"
      >
        {editingMember ? 'Update' : 'Add'} Member
      </button>
    </form>
  );
};

export default MemberForm;
